import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Input,
  Alert,
  Avatar,
  Chip,
  Button,
  Skeleton,
  Table,
  TableHeader,
  TableColumn,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  type SharedSelection,
} from '@/components/ui';
import {
  DataTable,
  type Column,
  type Pagination,
} from '@/components/common/table';
import { getAvatarColorClass } from '@/shared/lib/avatar';
import { StandardIcon } from '@/components/common/icon';
import { ProfileDrawer2 } from '@/components/people/people2/ProfileDrawer2';
import { useApiWithAuth } from '@/hooks/api';
import type { ApiResponse } from '@/shared/types/api';
import { useDebounce } from '@/hooks/debounce';
import { isProbablyPhone, formatPhone } from '@/lib/phone';
import { getInitialsFromFirstLast } from '@/shared/lib/names';
import { Icon } from '@iconify/react';

type People2SearchType = 'name' | 'email' | 'phone';

interface People2Person {
  pco_id: string;
  first_name: string;
  last_name: string;
  primary_email: string | null;
  primary_phone: string | null;
  primary_campus_id: string | null;
  status: string | null;
}

interface People2Row {
  id: string;
  data: People2Person;
}

interface CampusItem {
  pco_id: string;
  name: string;
}

function inferSearchType(search: string): People2SearchType {
  if (search.includes('@')) {
    return 'email';
  }

  if (isProbablyPhone(search)) {
    return 'phone';
  }

  return 'name';
}

function getPersonName(person: People2Person): string {
  const fullName =
    `${person.first_name || ''} ${person.last_name || ''}`.trim();
  if (fullName) {
    return fullName;
  }

  return person.primary_email || person.primary_phone || person.pco_id;
}

/** Status display: small colored dot + uppercase label (icon and text same color) */
function StatusCell({ status }: { status: string | null }) {
  const raw = (status || '').trim().toLowerCase();
  const label = raw ? raw.toUpperCase() : '-';
  let dotClass = 'bg-slate-300';
  let textClass = 'text-slate-500';
  if (raw === 'active') {
    dotClass = 'bg-emerald-400';
    textClass = 'text-emerald-500';
  } else if (raw === 'inactive') {
    dotClass = 'bg-slate-300';
    textClass = 'text-slate-500';
  } else if (raw === 'pending') {
    dotClass = 'bg-amber-400';
    textClass = 'text-amber-500';
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold ${textClass}`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
        aria-hidden
      />
      {label}
    </span>
  );
}

const STATUS_OPTIONS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

interface FilterPeopleModalProps {
  isOpen: boolean;
  onClose: () => void;
  campuses: CampusItem[];
  initialCampusId: string;
  initialStatus: string;
  onApply: (campusId: string, status: string) => void;
}

function FilterPeopleModal({
  isOpen,
  onClose,
  campuses,
  initialCampusId,
  initialStatus,
  onApply,
}: FilterPeopleModalProps) {
  const [pendingCampusId, setPendingCampusId] = useState(initialCampusId);
  const [pendingStatus, setPendingStatus] = useState(initialStatus);

  useEffect(() => {
    if (isOpen) {
      setPendingCampusId(initialCampusId);
      setPendingStatus(initialStatus);
    }
  }, [isOpen, initialCampusId, initialStatus]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <ModalContent>
        {(onModalClose) => (
          <>
            <ModalHeader className='flex items-center gap-2'>
              <Icon
                icon='mdi:filter-variant'
                width={20}
                height={20}
                className='text-primary'
              />
              Filter People
            </ModalHeader>
            <ModalBody className='gap-5'>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Campus
                </p>
                <Select
                  placeholder='All campuses'
                  selectedKeys={pendingCampusId ? [pendingCampusId] : []}
                  onSelectionChange={(keys) => {
                    const sel = keys as SharedSelection;
                    setPendingCampusId(sel.currentKey ?? '');
                  }}
                  selectionMode='single'
                  aria-label='Select campus'
                >
                  {campuses.map((c) => (
                    <SelectItem key={c.pco_id}>{c.name}</SelectItem>
                  ))}
                </Select>
                {pendingCampusId && (
                  <button
                    type='button'
                    className='mt-1.5 text-xs text-primary hover:underline'
                    onClick={() => setPendingCampusId('')}
                  >
                    Clear selection
                  </button>
                )}
              </div>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Status
                </p>
                <div className='flex flex-wrap gap-2'>
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.key}
                      type='button'
                      onClick={() =>
                        setPendingStatus(pendingStatus === s.key ? '' : s.key)
                      }
                      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                        pendingStatus === s.key
                          ? 'border-primary bg-primary text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant='flat' onPress={onModalClose}>
                Cancel
              </Button>
              <Button
                color='primary'
                onPress={() => {
                  onApply(pendingCampusId, pendingStatus);
                  onModalClose();
                }}
              >
                Apply Filters
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

const DISPLAY_PAGE_SIZE = 20;
const LOAD_PAGE_SIZE = 100;
const CAMPUSES_FETCH_LIMIT = 500;

const PEOPLE2_COLUMNS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'campus', label: 'Campus' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: '' },
];

function People2TableSkeleton({
  columns,
  rowCount = DISPLAY_PAGE_SIZE,
}: {
  columns: typeof PEOPLE2_COLUMNS;
  rowCount?: number;
}) {
  return (
    <Table
      isHeaderSticky
      className='max-h-[calc(100vh-220px)] text-sm text-gray-800'
    >
      <TableHeader columns={columns}>
        {(col) => <TableColumn key={col.key}>{col.label}</TableColumn>}
      </TableHeader>
      <tbody className='divide-y divide-slate-100'>
        {Array.from({ length: rowCount }).map((_, i) => (
          <tr key={i}>
            <td className='px-3 py-2 align-middle'>
              <div className='flex items-center gap-3'>
                <Skeleton className='h-8 w-8 shrink-0 rounded-full' />
                <Skeleton className='h-4 w-32 rounded-md' />
              </div>
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-40 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-28 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-6 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-16 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-8 w-8 rounded-md' />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function People2() {
  const { invoke, auth } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;

  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [chunks, setChunks] = useState<Record<number, People2Row[]>>({});
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 350);
  const [page, setPage] = useState(1);
  const [displayPageSize, setDisplayPageSize] = useState(DISPLAY_PAGE_SIZE);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterCampusId, setFilterCampusId] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [profileModalPcoId, setProfileModalPcoId] = useState<string | null>(
    null,
  );

  const campusLookup = useMemo(
    () =>
      campuses.reduce<Record<string, string>>((acc, c) => {
        acc[c.pco_id] = c.name;

        return acc;
      }, {}),
    [campuses],
  );

  const columns = useMemo(
    (): Column<People2Row>[] => [
      {
        key: 'name',
        label: 'Name',
        display: ({ item }) => {
          const name = getPersonName(item.data);
          const initials =
            getInitialsFromFirstLast(
              item.data.first_name,
              item.data.last_name,
            ) || item.data.pco_id.slice(0, 2).toUpperCase();
          return (
            <div className='flex items-center gap-3'>
              <Avatar
                name={name}
                getInitials={() => initials}
                className={getAvatarColorClass(name)}
                size='sm'
              />
              <span className='font-medium text-slate-800'>{name}</span>
            </div>
          );
        },
      },
      {
        key: 'email',
        label: 'Email',
        display: ({ item }) => (
          <span className='text-slate-700'>
            {item.data.primary_email || '-'}
          </span>
        ),
      },
      {
        key: 'phone',
        label: 'Phone',
        display: ({ item }) => (
          <span className='text-slate-700'>
            {formatPhone(item.data.primary_phone)}
          </span>
        ),
      },
      {
        key: 'campus',
        label: 'Campus',
        display: ({ item }) => {
          const campusId = item.data.primary_campus_id;
          const label = campusId
            ? (campusLookup[String(campusId)] ?? campusId)
            : null;
          if (label === null || label === undefined) {
            return <></>;
          }
          return (
            <Chip className='rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700'>
              {label}
            </Chip>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        display: ({ item }) => <StatusCell status={item.data.status} />,
      },
      {
        key: 'actions',
        label: '',
        display: ({ item }) => (
          <Button
            isIconOnly
            variant='light'
            size='sm'
            className='w-8 min-w-8 text-slate-500'
            aria-label={`Open ${getPersonName(item.data)}`}
            onPress={() => {
              setProfileModalPcoId(item.data.pco_id);
            }}
          >
            <Icon icon='mdi:open-in-new' width={20} height={20} />
          </Button>
        ),
      },
    ],
    [campusLookup],
  );

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadCampuses = async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', String(CAMPUSES_FETCH_LIMIT));
        params.set('offset', '0');
        const response = (await invokeRef.current(
          `/pco-campuses?${params.toString()}`,
        )) as ApiResponse<CampusItem[]>;
        if (cancelled) {
          return;
        }
        const list =
          response?.data && Array.isArray(response.data)
            ? response.data.map((c: CampusItem) => ({
                pco_id: c.pco_id,
                name: c.name,
              }))
            : [];
        setCampuses(list);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Failed loading campuses for People 2:', err);
        setCampuses([]);
      }
    };

    loadCampuses();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  const search = debouncedSearchInput.trim();
  const searchType = search ? inferSearchType(search) : '';

  const startIndex = (page - 1) * displayPageSize;
  const chunkIndex = Math.floor(startIndex / LOAD_PAGE_SIZE);
  const startInChunk = startIndex % LOAD_PAGE_SIZE;

  const currentChunk = chunks[chunkIndex];
  const displayedItems = useMemo(() => {
    if (!currentChunk) {
      return [];
    }

    return currentChunk.slice(startInChunk, startInChunk + displayPageSize);
  }, [currentChunk, startInChunk, displayPageSize]);

  const hasChunkForPage = currentChunk !== undefined;

  useEffect(() => {
    if (hasChunkForPage) {
      return () => {};
    }

    const offset = chunkIndex * LOAD_PAGE_SIZE;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('limit', String(LOAD_PAGE_SIZE));
        params.set('offset', String(offset));
        if (filterCampusId) {
          params.set('campusId', filterCampusId);
        }
        if (filterStatus) {
          params.set('status', filterStatus);
        }
        if (search) {
          params.set('search', search);
          params.set('searchType', searchType);
        } else if (chunkIndex === 0) {
          params.set('count', 'true');
        }

        const response = (await invokeRef.current(
          `/people2?${params.toString()}`,
        )) as ApiResponse<People2Person[]> & { count?: number };
        if (cancelled) {
          return;
        }

        const rows = (response.data || []).map((person: People2Person) => ({
          id: person.pco_id,
          data: person,
        }));
        setChunks((prev) => ({ ...prev, [chunkIndex]: rows }));
        if (response.count !== undefined) {
          setTotalCount(response.count);
        } else if (search) {
          setTotalCount(null);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading People 2:', err);
        setChunks((prev) => {
          const next = { ...prev };
          delete next[chunkIndex];

          return next;
        });
        setError('Unable to load People 2 data');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    auth.firebaseUser?.uid,
    debouncedSearchInput,
    chunkIndex,
    hasChunkForPage,
    search,
    searchType,
    filterCampusId,
    filterStatus,
  ]);

  useEffect(() => {
    setPage(1);
    setChunks({});
    setTotalCount(null);
  }, [debouncedSearchInput, filterCampusId, filterStatus]);

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      absolute: true,
      page,
      itemsPerPage: displayPageSize,
      allowItemsPerPage: [10, 20, 50, 100],
      onPageChange: (nextPage: number) => setPage(nextPage),
      onItemsPerPageChange: (value: number) => {
        setDisplayPageSize(value);
        setPage(1);
      },
    }),
    [page, displayPageSize],
  );

  const hasActiveFilters = Boolean(filterCampusId || filterStatus);

  return (
    <div>
      <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <Input
          className='basis-1/2'
          name='people-search'
          value={searchInput}
          onValueChange={setSearchInput}
          isClearable
          onClear={() => setSearchInput('')}
          placeholder='Search by name, email or phone'
          startContent={<StandardIcon id='mdi:search' />}
          autoComplete='off'
        />
        <Button
          variant='flat'
          className={`relative border border-slate-200 bg-white text-slate-700 ${hasActiveFilters ? 'border-primary/50 text-primary' : ''}`}
          startContent={
            <Icon icon='mdi:filter-variant' width={18} height={18} />
          }
          onPress={() => setIsFilterOpen(true)}
        >
          Filter
          {hasActiveFilters && (
            <span className='absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-primary' />
          )}
        </Button>
      </div>
      <FilterPeopleModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        campuses={campuses}
        initialCampusId={filterCampusId}
        initialStatus={filterStatus}
        onApply={(campusId, status) => {
          setFilterCampusId(campusId);
          setFilterStatus(status);
        }}
      />
      <ProfileDrawer2
        isOpen={profileModalPcoId !== null}
        onClose={() => setProfileModalPcoId(null)}
        pcoId={profileModalPcoId}
        campusLookup={campusLookup}
      />
      {error ? (
        <Alert color='danger' className='mb-2'>
          {error}
        </Alert>
      ) : null}
      {loading && !hasChunkForPage ? (
        <People2TableSkeleton
          columns={PEOPLE2_COLUMNS}
          rowCount={displayPageSize}
        />
      ) : (
        <DataTable
          aria-label='People 2'
          columns={columns}
          items={displayedItems}
          count={
            (totalCount ??
              Object.values(chunks).reduce((sum, c) => sum + c.length, 0)) ||
            displayedItems.length
          }
          pagination={pagination}
          paginationVariant='numbered'
          loading={loading && !hasChunkForPage}
          className='text-sm text-gray-800'
          empty={{
            icon: 'mdi:account-search',
            title: 'No people found',
            body: "We couldn't find anyone matching your current selection. Try adjusting your filters or adding a new person to your organization.",
            actions: (
              <>
                <Button
                  color='primary'
                  startContent={
                    <Icon icon='mdi:account-plus' width={18} height={18} />
                  }
                >
                  Add Person
                </Button>
                <Button variant='flat' onPress={() => setSearchInput('')}>
                  Clear Filters
                </Button>
              </>
            ),
          }}
        />
      )}
    </div>
  );
}
